import { Action, Reducer } from 'redux';
import { IPublicClientApplication } from '@azure/msal-browser';
import * as TeamsState from './TeamsState';
import { TokenHelper } from '../TokenHelper';
import { AppThunkAction } from '.';
import { SortHelper } from '../SortHelper';
import { KnownContactsAction } from './contacts';
import { KnownCustomersAction } from './customers';
import { ConsultantInvoice, ConsultantPeppolReceivedDoc, PeppolInvoiceResponse, PeppolSendedDoc } from '../interfaces/Consultant';
import { ContactProjectRole } from '../interfaces/ContactCustomerRole';
import { KnownConsultantAction } from './consultants';

// -----------------
// STATE - This defines the type of data maintained in the Redux store.

export interface InvoiceTemplate {
    uniqueId: string,
    displayName: string,
    order: number,
    internalName: string,
}

export interface Project {
    projectid: string | null,
    customerid: string | null,
    displayName: string,
    actif: boolean,
    prixForfaitaire: number | null,
    dateDebut: string | null,
    dateFin: string | null,
    description: string | null,
    contacts: ContactProjectRole[] | null,
    contactsLoading?: boolean,
}

export interface ProjectAssigment {
    projectAssignmentid: string | null,
    projectid: string | null,
    displayName: string | null,
    consultantid: string | null,
    consultantCode: string | null,
    consultantPrice: number,
    consultantVatcode: number,
    customerPrice: number,
    customerVatcode: number,
    dateDebut: string | null,
    dateFin: string | null,
    customerSignedTimeSheetNeeded: boolean,
    po: string | null,
    poquantity: number | null,
    unitOfTime: string,
    description: string | null,
    orderSharePointFileUniqueId: string | null,
    sellerItemIdentification: string | null,
    buyerItemIdentification: string | null,
    peppolAttachmentInvoiceName: string | null,
    peppolAttachmentTsname: string | null,
    dateDebutTsencoding: string | null,
    dateFinTsencoding: string | null,
}

export interface TimeSheetEntry {
    timesheetEntryid: string | null,
    projectAssignmentid: string | null,
    date: string | null,
    count: number,
    invoiceid: string | null,
    consultantInvoiceid?: string | null,
    description: string | null,
    timeSheetFileExist: boolean | undefined,
    invoiceDescription: string | null,
}

export interface InvoiceEntry {
    invoiceEntryid: string | null,
    invoiceid: string | null,
    sellPrice: number,
    costPrice: number,
    unitCode: string,
    count: number,
    vatcode: number,
    description: string | null,
    sellerItemIdentification: string | null,
    buyerItemIdentification: string | null,
    orderNumber: number,
}

export interface Invoice {
    invoiceid: string | null,
    customerid: string | null,
    invoiceRef: string | null,
    invoiceDate: string | null,
    paymentDate: string | null,
    payment: number,
    lock: boolean,
    isCreditNote: boolean,
    invoiceSharePointFileUniqueId: string | null,
    vatcode: number,
    description: string | null,
    buyerReference: string | null,
    invoiceDescription: string | null,
    emailSent?: string,
    peppolSent?: string,
    consultantid: string,
    pdfuploaded: boolean,
    prepaidAmount: number | null,
    invoiceEntries?: InvoiceEntry[],
    timeSheetEntryInvoices?: string[],
    timeSheetEntryConsultantInvoices?: string[],
    sellEntries?: string[],
    peppolSendedDocs?: PeppolSendedDoc[],
    peppolInvoiceResponse?: PeppolInvoiceResponse[],
}
export interface Product {
    productid: string | null,
    displayName: string | null,
    description: string | null,
}

export interface Article {
    articleid: string | null,
    productid: string | null,
    unitPrice: number,
    displayName: string | null,
    description: string | null,
    isActif: boolean | null,
}

export interface Sale {
    sellid: string | null,
    date: string | null,
    articleid: string | null,
    customerid: string | null,
    description: string | null,
    count: number,
    unitPrice: number,
    vatcode: number,
    invoiceid: string | null,
    orderRef: string | null,
    orderSharePointFileUniqueId: string | null,
    sellerItemIdentification: string | null,
    buyerItemIdentification: string | null,
}

export interface FileUploadDescription {
    timeSheetEntry: TimeSheetEntry | null,
    sale: Sale | null,
    invoice: Invoice | null,
    consultantInvoice: ConsultantInvoice | null,
    projectAssigment: ProjectAssigment | null,
}

export interface DataState {
    projects: Project[] | undefined;
    projectEdit: Project | null;
    projectDelete: Project | null;
    projectsLoading: boolean;
    projectsAssigments: ProjectAssigment[] | undefined;
    projectAssigmentEdit: ProjectAssigment | null;
    projectAssigmentDelete: ProjectAssigment | null;
    projectContactsEdit: Project | null;

    projectsAssigmentsLoading: boolean;
    timeSheetEntries: TimeSheetEntry[] | undefined;
    timeSheetEdit: TimeSheetEntry | null;
    timeSheetDelete: TimeSheetEntry | null;
    timeSheetEntriesLoading: boolean;
    invoices: Invoice[] | undefined;
    invoiceEdit: Invoice | null;
    invoiceToDelete: Invoice | null;
    invoiceGeneration: Invoice | null;
    invoiceEmail: Invoice | null;
    invoiceSendUBL: Invoice | null;
    invoicesLoading: boolean;
    products: Product[] | undefined;
    productsLoading: boolean;
    articles: Article[] | undefined;
    articlesLoading: boolean;
    sales: Sale[] | undefined;
    salesEdit: Sale | null;
    salesLoading: boolean;

    invoiceTemplates: InvoiceTemplate[] | undefined;
    invoiceTemplatesLoading: boolean;

    FileUpload: FileUploadDescription | null;

    invoiceQuickView: Invoice | null;
    projectQuickView: Project | null;
    projectAssigmentQuickView: ProjectAssigment | null;
    timesheetQuickView: TimeSheetEntry | null;
    productQuickView: Product | null;
    salesQuickView: Sale | null;

    peppolReceivedDocEdit: ConsultantPeppolReceivedDoc | null;
}

// -----------------
// ACTIONS - These are serializable (hence replayable) descriptions of state transitions.
// They do not themselves have any side-effects; they just describe something that is going to happen.
// Use @typeName and isActionType for type detection that works even after serialization/deserialization.

interface ReloadProjectsAction { type: 'RELOAD_PROJECTS' }
interface RequestProjectsAction { type: 'REQUEST_PROJECTS' }
interface ReceiveProjectsAction { type: 'RECEIVE_PROJECTS'; projects: Project[]; }
interface RequestProjectContactsAction { type: 'REQUEST_PROJECTCONTACTS'; projectid: string; }
interface ReceiveProjectContactsAction { type: 'RECEIVE_PROJECTCONTACTS'; projectid: string; contactProjectRoles: ContactProjectRole[] }

interface EditProjectsAction { type: 'EDIT_PROJECT'; project: Project | null; }
interface DeleteProjectsAction { type: 'DELETE_PROJECT'; project: Project | null; }
interface PatchDeleteProjectAction { type: 'PATCHDELETE_PROJECT'; project: Project; }
interface PatchProjectAction { type: 'PATCH_PROJECT'; project: Project; }
interface EditProjectContactsAction { type: 'EDIT_PROJECTCONTACTS'; project: Project | null; }

interface ReloadProjectsAssigmentsAction { type: 'RELOAD_PROJECTASSIGMENT' }
interface RequestProjectsAssigmentsAction { type: 'REQUEST_PROJECTSASSIGMENTS' }
interface ReceiveProjectsAssigmentsAction { type: 'RECEIVE_PROJECTSASSIGMENTS'; projectAssigments: ProjectAssigment[]; }
interface EditProjectAssigmentAction { type: 'EDIT_PROJECTASSIGMENT'; projectAssigment: ProjectAssigment | null; }
interface DeleteProjectAssigmentAction { type: 'DELETE_PROJECTASSIGMENT'; projectAssigment: ProjectAssigment | null; }
interface PatchDeleteProjectAssigmentAction { type: 'PATCHDELETE_PROJECTASSIGMENT'; projectAssigment: ProjectAssigment; }
interface PatchProjectAssigmentAction { type: 'PATCH_PROJECTASSIGMENT'; projectAssigment: ProjectAssigment; }
interface RequestTimeSheetEntriesAction { type: 'REQUEST_TIMESHEETENTRIES' }
interface ReceiveTimeSheetEntriesAction { type: 'RECEIVE_TIMESHEETENTRIES'; timeSheetEntries: TimeSheetEntry[]; }
interface ReloadTimeSheetEntryAction { type: 'RELOAD_TIMESHEETS' }
interface EditTimeSheetEntryAction { type: 'EDIT_TIMESHEET'; timeSheetEntry: TimeSheetEntry | null; }
interface PatchTimeSheetEntryAction { type: 'PATCH_TIMESHEET'; timeSheetEntry: TimeSheetEntry; }
interface DeleteTimeSheetEntryAction { type: 'DELETE_TIMESHEET'; timeSheetEntry: TimeSheetEntry | null; }
interface PatchDeleteTimeSheetEntryAction { type: 'PATCHDELETE_TIMESHEET'; timeSheetEntry: TimeSheetEntry; }
interface ReloadInvoicesAction { type: 'RELOAD_INVOICES' }
interface RequestInvoicesAction { type: 'REQUEST_INVOICES' }
interface ReceiveInvoicesAction { type: 'RECEIVE_INVOICES'; invoices: Invoice[]; }
interface EditInvoiceAction { type: 'EDIT_INVOICES'; invoice: Invoice | null; }
interface GenerateInvoiceAction { type: 'GEN_INVOICES'; invoice: Invoice | null; }
interface EmailInvoiceAction { type: 'EMAIL_INVOICES'; invoice: Invoice | null; }
interface SendUBLInvoiceAction { type: 'SENDUBL_INVOICES'; invoice: Invoice | null; }
interface PatchInvoiceAction { type: 'PATCH_INVOICES'; invoice: Invoice; }
interface DeleteInvoicesAction { type: 'DELETE_INVOICES'; invoice: Invoice | null; }
interface PatchDeleteInvoiceAction { type: 'PATCHDELETE_INVOICES'; invoice: Invoice; }
interface RequestInvoiceTemplatesAction { type: 'REQUEST_INVOICETEMPLATES' }
interface ReceiveInvoiceTemplatesAction { type: 'RECEIVE_INVOICEETEMPLATES'; invoiceTemplates: InvoiceTemplate[]; }
interface RequestProductsAction { type: 'REQUEST_PRODUCTS' }
interface ReceiveProductsAction { type: 'RECEIVE_PRODUCTS'; products: Product[]; }
interface RequestArticlesAction { type: 'REQUEST_ARTICLES' }
interface ReceiveArticlesAction { type: 'RECEIVE_ARTICLES'; articles: Article[]; }
interface RequestSalesAction { type: 'REQUEST_SALES' }
interface ReceiveSalesAction { type: 'RECEIVE_SALES'; sales: Sale[]; }
interface EditSalesAction { type: 'EDIT_SALES'; sale: Sale | null; }
interface PatchSalesAction { type: 'PATCH_SALES'; sale: Sale; }
interface RequestInvoiceEntriesAction { type: 'REQUEST_INVOICEENTRIES' }
interface ReceiveInvoiceEntriesAction { type: 'RECEIVE_INVOICEENTRIES'; InvoiceEntries: InvoiceEntry[]; }

interface FileUploadAction { type: 'FILEUPLOAD'; dest: FileUploadDescription | null; }

interface QuickViewClearAction { type: 'QV_CLEAR'; }
interface QuickViewInvoiceAction { type: 'QV_INVOICE'; invoiceRef: Invoice | null; }
interface QuickViewProjectAction { type: 'QV_PROJECT'; project: Project | null; }
interface QuickViewProjectAssigmentAction { type: 'QV_PROJECTASSIGMENT'; projectAssigment: ProjectAssigment | null; }
interface QuickViewTimesheetAction { type: 'QV_TIMESHEET'; timeSheetEntry: TimeSheetEntry | null; }
interface QuickViewProductAction { type: 'QV_PRODUCT'; product: Product | null; }
interface QuickViewSalesAction { type: 'QV_SALES'; sale: Sale | null; }
interface EditReceivedPeppolInvoice { type: 'EDIT_PEPPOLRECEIVEDINVOICE'; peppolReceivedDoc: ConsultantPeppolReceivedDoc | null; }

// Declare a 'discriminated union' type. This guarantees that all references to 'type' properties contain one of the
// declared type strings (and not any other arbitrary string).
export type KnownDataAction = ReloadProjectsAction | RequestProjectsAction | ReceiveProjectsAction | EditProjectsAction | DeleteProjectsAction | PatchDeleteProjectAction | PatchProjectAction | EditProjectContactsAction | RequestProjectContactsAction | ReceiveProjectContactsAction
    | ReloadProjectsAssigmentsAction | RequestProjectsAssigmentsAction | ReceiveProjectsAssigmentsAction | EditProjectAssigmentAction | DeleteProjectAssigmentAction | PatchDeleteProjectAssigmentAction | PatchProjectAssigmentAction
    | ReloadTimeSheetEntryAction | RequestTimeSheetEntriesAction | ReceiveTimeSheetEntriesAction | EditTimeSheetEntryAction | PatchTimeSheetEntryAction | DeleteTimeSheetEntryAction | PatchDeleteTimeSheetEntryAction
    | ReloadInvoicesAction | RequestInvoicesAction | ReceiveInvoicesAction | EditInvoiceAction | DeleteInvoicesAction | PatchInvoiceAction | PatchDeleteInvoiceAction | GenerateInvoiceAction | EmailInvoiceAction | SendUBLInvoiceAction
    | RequestInvoiceTemplatesAction | ReceiveInvoiceTemplatesAction
    | RequestProductsAction | ReceiveProductsAction
    | RequestArticlesAction | ReceiveArticlesAction
    | RequestSalesAction | ReceiveSalesAction | EditSalesAction | PatchSalesAction
    | RequestInvoiceEntriesAction | ReceiveInvoiceEntriesAction
    | FileUploadAction
    | QuickViewClearAction | QuickViewInvoiceAction | QuickViewProjectAction | QuickViewProjectAssigmentAction | QuickViewTimesheetAction | QuickViewProductAction | QuickViewSalesAction  //| QuickViewContactAction
    | EditReceivedPeppolInvoice
    ;

// ----------------
// ACTION CREATORS - These are functions exposed to UI components that will trigger a state transition.
// They don't directly mutate state, but they can have external side-effects (such as loading data).

export const dataActionCreators = {
    requestProjects: (msalInstance: IPublicClientApplication, teamsState: TeamsState.TeamsState | undefined): AppThunkAction<KnownDataAction> => (dispatch, getState) => {
        dispatch({ type: 'REQUEST_PROJECTS' });
        // Only load data if it's something we don't already have (and are not already loading)
        const appState = getState();
        if (appState && appState.userNeomyticLogin) {
            TokenHelper.GetToken(msalInstance, teamsState)
                .then(token => {
                    if (token !== null) {
                        fetch(`api/Projects`, {
                            method: "get",
                            headers: {
                                "Content-Type": "application/json",
                                authorization: "bearer " + token,
                            },
                            mode: "cors",
                            cache: "default",
                        })
                            .then(response => {
                                if (response.status === 403) {
                                    //dispatch({ type: 'RECEIVE_USER_LOGIN_ACCESSDENIED' });
                                }
                                return response.json() as Promise<Project[]>;
                            })
                            .then(data => {
                                dispatch({ type: 'RECEIVE_PROJECTS', projects: data });
                            });
                    }
                });
        }
    },
    requestProjectContacts: (msalInstance: IPublicClientApplication, teamsState: TeamsState.TeamsState | undefined, projectid: string): AppThunkAction<KnownDataAction> => (dispatch, getState) => {
        // Only load data if it's something we don't already have (and are not already loading)
        const appState = getState();
        if (appState && appState.data) {
            TokenHelper.GetToken(msalInstance, teamsState)
                .then(token => {
                    if (token !== null) {
                        dispatch({ type: 'REQUEST_PROJECTCONTACTS', projectid: projectid });
                        fetch(`api/Projects/` + projectid + "/Contacts", {
                            method: "get",
                            headers: {
                                "Content-Type": "application/json",
                                authorization: "bearer " + token,
                            },
                            mode: "cors",
                            cache: "default",
                        })
                            .then(response => {
                                if (response.status === 403) {
                                    //dispatch({ type: 'RECEIVE_USER_LOGIN_ACCESSDENIED' });
                                }
                                return response.json() as Promise<ContactProjectRole[]>;
                            })
                            .then(data => {
                                dispatch({ type: 'RECEIVE_PROJECTCONTACTS', projectid: projectid, contactProjectRoles: data });
                            });
                    }
                });
        }
    },
    requestProjectsAssigments: (msalInstance: IPublicClientApplication, teamsState: TeamsState.TeamsState | undefined): AppThunkAction<KnownDataAction> => (dispatch, getState) => {
        dispatch({ type: 'REQUEST_PROJECTSASSIGMENTS' });
        // Only load data if it's something we don't already have (and are not already loading)
        const appState = getState();
        if (appState && appState.userNeomyticLogin) {
            TokenHelper.GetToken(msalInstance, teamsState)
                .then(token => {
                    if (token !== null) {
                        fetch(`api/ProjectsAssigments`, {
                            method: "get",
                            headers: {
                                "Content-Type": "application/json",
                                authorization: "bearer " + token,
                            },
                            mode: "cors",
                            cache: "default",
                        })
                            .then(response => {
                                if (response.status === 403) {
                                    //dispatch({ type: 'RECEIVE_USER_LOGIN_ACCESSDENIED' });
                                }
                                return response.json() as Promise<ProjectAssigment[]>;
                            })
                            .then(data => {
                                dispatch({ type: 'RECEIVE_PROJECTSASSIGMENTS', projectAssigments: data });
                            });
                    }
                });
        }
    },
    requestTimeSheetEntries: (msalInstance: IPublicClientApplication, teamsState: TeamsState.TeamsState | undefined): AppThunkAction<KnownDataAction> => (dispatch, getState) => {
        dispatch({ type: 'REQUEST_TIMESHEETENTRIES' });
        // Only load data if it's something we don't already have (and are not already loading)
        const appState = getState();
        if (appState && appState.userNeomyticLogin) {
            TokenHelper.GetToken(msalInstance, teamsState)
                .then(token => {
                    if (token !== null) {
                        fetch(`api/TimeSheets`, {
                            method: "get",
                            headers: {
                                "Content-Type": "application/json",
                                authorization: "bearer " + token,
                            },
                            mode: "cors",
                            cache: "default",
                        })
                            .then(response => {
                                if (response.status === 403) {
                                    //dispatch({ type: 'RECEIVE_USER_LOGIN_ACCESSDENIED' });
                                }
                                return response.json() as Promise<TimeSheetEntry[]>;
                            })
                            .then(data => {
                                dispatch({ type: 'RECEIVE_TIMESHEETENTRIES', timeSheetEntries: data });
                            });
                    }
                });
        }
    },
    requestInvoices: (msalInstance: IPublicClientApplication, teamsState: TeamsState.TeamsState | undefined): AppThunkAction<KnownDataAction> => (dispatch, getState) => {
        dispatch({ type: 'REQUEST_INVOICES' });
        // Only load data if it's something we don't already have (and are not already loading)
        const appState = getState();
        if (appState && appState.userNeomyticLogin) {
            TokenHelper.GetToken(msalInstance, teamsState)
                .then(token => {
                    if (token !== null) {
                        fetch(`api/Invoices`, {
                            method: "get",
                            headers: {
                                "Content-Type": "application/json",
                                authorization: "bearer " + token,
                            },
                            mode: "cors",
                            cache: "default",
                        })
                            .then(response => {
                                if (response.status === 403) {
                                    //dispatch({ type: 'RECEIVE_USER_LOGIN_ACCESSDENIED' });
                                }
                                return response.json() as Promise<Invoice[]>;
                            })
                            .then(data => {
                                dispatch({ type: 'RECEIVE_INVOICES', invoices: data });
                            });
                    }
                });
        }
    },
    requestProducts: (msalInstance: IPublicClientApplication, teamsState: TeamsState.TeamsState | undefined): AppThunkAction<KnownDataAction> => (dispatch, getState) => {
        dispatch({ type: 'REQUEST_PRODUCTS' });
        // Only load data if it's something we don't already have (and are not already loading)
        const appState = getState();
        if (appState && appState.userNeomyticLogin) {
            TokenHelper.GetToken(msalInstance, teamsState)
                .then(token => {
                    if (token !== null) {
                        fetch(`api/Products`, {
                            method: "get",
                            headers: {
                                "Content-Type": "application/json",
                                authorization: "bearer " + token,
                            },
                            mode: "cors",
                            cache: "default",
                        })
                            .then(response => {
                                if (response.status === 403) {
                                    //dispatch({ type: 'RECEIVE_USER_LOGIN_ACCESSDENIED' });
                                }
                                return response.json() as Promise<Product[]>;
                            })
                            .then(data => {
                                dispatch({ type: 'RECEIVE_PRODUCTS', products: data });
                            });
                    }
                });
        }
    },
    requestArticles: (msalInstance: IPublicClientApplication, teamsState: TeamsState.TeamsState | undefined): AppThunkAction<KnownDataAction> => (dispatch, getState) => {
        dispatch({ type: 'REQUEST_ARTICLES' });
        // Only load data if it's something we don't already have (and are not already loading)
        const appState = getState();
        if (appState && appState.userNeomyticLogin) {
            TokenHelper.GetToken(msalInstance, teamsState)
                .then(token => {
                    if (token !== null) {
                        fetch(`api/Articles`, {
                            method: "get",
                            headers: {
                                "Content-Type": "application/json",
                                authorization: "bearer " + token,
                            },
                            mode: "cors",
                            cache: "default",
                        })
                            .then(response => {
                                if (response.status === 403) {
                                    //dispatch({ type: 'RECEIVE_USER_LOGIN_ACCESSDENIED' });
                                }
                                return response.json() as Promise<Article[]>;
                            })
                            .then(data => {
                                dispatch({ type: 'RECEIVE_ARTICLES', articles: data });
                            });
                    }
                });
        }
    },
    requestSells: (msalInstance: IPublicClientApplication, teamsState: TeamsState.TeamsState | undefined): AppThunkAction<KnownDataAction> => (dispatch, getState) => {
        dispatch({ type: 'REQUEST_SALES' });
        // Only load data if it's something we don't already have (and are not already loading)
        const appState = getState();
        if (appState && appState.userNeomyticLogin) {
            TokenHelper.GetToken(msalInstance, teamsState)
                .then(token => {
                    if (token !== null) {
                        fetch(`api/Sales`, {
                            method: "get",
                            headers: {
                                "Content-Type": "application/json",
                                authorization: "bearer " + token,
                            },
                            mode: "cors",
                            cache: "default",
                        })
                            .then(response => {
                                if (response.status === 403) {
                                    return [];
                                }
                                return response.json() as Promise<Sale[]>;
                            })
                            .then(data => {
                                dispatch({ type: 'RECEIVE_SALES', sales: data });
                            });
                    }
                });
        }
    },
    requestInvoiceEntries: (msalInstance: IPublicClientApplication, teamsState: TeamsState.TeamsState | undefined): AppThunkAction<KnownDataAction> => (dispatch, getState) => {
        dispatch({ type: 'REQUEST_INVOICEENTRIES' });
        // Only load data if it's something we don't already have (and are not already loading)
        const appState = getState();
        if (appState && appState.userNeomyticLogin) {
            TokenHelper.GetToken(msalInstance, teamsState)
                .then(token => {
                    if (token !== null) {
                        fetch(`api/InvoiceEntries`, {
                            method: "get",
                            headers: {
                                "Content-Type": "application/json",
                                authorization: "bearer " + token,
                            },
                            mode: "cors",
                            cache: "default",
                        })
                            .then(response => {
                                if (response.status === 403) {
                                    //dispatch({ type: 'RECEIVE_USER_LOGIN_ACCESSDENIED' });
                                }
                                return response.json() as Promise<InvoiceEntry[]>;
                            })
                            .then(data => {
                                dispatch({ type: 'RECEIVE_INVOICEENTRIES', InvoiceEntries: data });
                            });
                    }
                });
        }
    },
    requestInvoiceTemplates: (msalInstance: IPublicClientApplication, teamsState: TeamsState.TeamsState | undefined, forceReload?: boolean): AppThunkAction<KnownDataAction> => (dispatch, getState) => {
        dispatch({ type: 'REQUEST_INVOICETEMPLATES' });
        // Only load data if it's something we don't already have (and are not already loading)
        const appState = getState();
        if (appState && appState.userNeomyticLogin) {
            TokenHelper.GetToken(msalInstance, teamsState)
                .then(token => {
                    if (token !== null) {
                        fetch(`api/Invoices/GetInvoiceTemplates?forceupdate=` + (forceReload ? forceReload.toString() : "false"), {
                            method: "get",
                            headers: {
                                "Content-Type": "application/json",
                                authorization: "bearer " + token,
                            },
                            mode: "cors",
                            cache: "default",
                        })
                            .then(response => {
                                if (response.status === 403) {
                                    //dispatch({ type: 'RECEIVE_USER_LOGIN_ACCESSDENIED' });
                                }
                                return response.json() as Promise<InvoiceTemplate[]>;
                            })
                            .then(data => {
                                dispatch({ type: 'RECEIVE_INVOICEETEMPLATES', invoiceTemplates: data });
                            });
                    }
                });
        }
    },
    ReloadProjects: (): AppThunkAction<KnownDataAction> => (dispatch/*, getState*/) => {
        dispatch({ type: 'RELOAD_PROJECTS' });
    },
    requestProjectEdition: (project: Project | null): AppThunkAction<KnownDataAction> => (dispatch/*, getState*/) => {
        dispatch({ type: 'EDIT_PROJECT', project: project });
    },
    patchProjectEdition: (project: Project): AppThunkAction<KnownDataAction> => (dispatch/*, getState*/) => {
        dispatch({ type: 'PATCH_PROJECT', project: project });
    },
    requestProjectDeletion: (project: Project | null): AppThunkAction<KnownDataAction> => (dispatch/*, getState*/) => {
        dispatch({ type: 'DELETE_PROJECT', project: project });
    },
    patchProjectDeletionEntryEdition: (project: Project): AppThunkAction<KnownDataAction> => (dispatch/*, getState*/) => {
        dispatch({ type: 'PATCHDELETE_PROJECT', project: project });
    },
    requestProjectContactsEdition: (project: Project | null): AppThunkAction<KnownDataAction> => (dispatch/*, getState*/) => {
        dispatch({ type: 'EDIT_PROJECTCONTACTS', project: project });
    },
    requestPatchProjectContacts: (projectid: string, contacts: ContactProjectRole[]): AppThunkAction<KnownDataAction> => (dispatch/*, getState*/) => {
        dispatch({ type: 'RECEIVE_PROJECTCONTACTS', projectid: projectid, contactProjectRoles: contacts });
    },
    ReloadProjectAssigment: (): AppThunkAction<KnownDataAction> => (dispatch/*, getState*/) => {
        dispatch({ type: 'RELOAD_PROJECTASSIGMENT' });
    },
    requestProjectAssigmentDeletion: (projectAssigment: ProjectAssigment | null): AppThunkAction<KnownDataAction> => (dispatch/*, getState*/) => {
        dispatch({ type: 'DELETE_PROJECTASSIGMENT', projectAssigment: projectAssigment });
    },
    requestProjectAssigmentEdition: (projectAssigment: ProjectAssigment | null): AppThunkAction<KnownDataAction> => (dispatch/*, getState*/) => {
        dispatch({ type: 'EDIT_PROJECTASSIGMENT', projectAssigment: projectAssigment });
    },
    patchProjectAssigmentEdition: (projectAssigment: ProjectAssigment): AppThunkAction<KnownDataAction> => (dispatch/*, getState*/) => {
        dispatch({ type: 'PATCH_PROJECTASSIGMENT', projectAssigment: projectAssigment });
    },
    patchProjectAssigmentDeletion: (projectAssigment: ProjectAssigment): AppThunkAction<KnownDataAction> => (dispatch/*, getState*/) => {
        dispatch({ type: 'PATCHDELETE_PROJECTASSIGMENT', projectAssigment: projectAssigment });
    },
    ReloadTimesheets: (): AppThunkAction<KnownDataAction> => (dispatch/*, getState*/) => {
        dispatch({ type: 'RELOAD_TIMESHEETS' });
    },
    requestTimeSheetEntryEdition: (timeSheetEntry: TimeSheetEntry | null): AppThunkAction<KnownDataAction> => (dispatch/*, getState*/) => {
        dispatch({ type: 'EDIT_TIMESHEET', timeSheetEntry: timeSheetEntry });
    },
    requestTimeSheetEntrydeletion: (timeSheetEntry: TimeSheetEntry | null): AppThunkAction<KnownDataAction> => (dispatch/*, getState*/) => {
        dispatch({ type: 'DELETE_TIMESHEET', timeSheetEntry: timeSheetEntry });
    },
    requestFileUploadTimeSheet: (Entry: TimeSheetEntry): AppThunkAction<KnownDataAction> => (dispatch/*, getState*/) => {
        dispatch({ type: 'FILEUPLOAD', dest: { timeSheetEntry: Entry, sale: null, invoice: null, consultantInvoice: null, projectAssigment: null } });
    },
    requestFileUploadSale: (Entry: Sale): AppThunkAction<KnownDataAction> => (dispatch/*, getState*/) => {
        dispatch({ type: 'FILEUPLOAD', dest: { timeSheetEntry: null, sale: Entry, invoice: null, consultantInvoice: null, projectAssigment: null } });
    },
    requestFileUploadProjectAssigment: (Entry: ProjectAssigment): AppThunkAction<KnownDataAction> => (dispatch/*, getState*/) => {
        dispatch({ type: 'FILEUPLOAD', dest: { timeSheetEntry: null, sale: null, invoice: null, consultantInvoice: null, projectAssigment: Entry } });
    },
    requestFileUploadInvoice: (Entry: Invoice): AppThunkAction<KnownDataAction> => (dispatch/*, getState*/) => {
        dispatch({ type: 'FILEUPLOAD', dest: { timeSheetEntry: null, sale: null, invoice: Entry, consultantInvoice: null, projectAssigment: null } });
    },
    requestFileUploadConsultantInvoice: (Entry: ConsultantInvoice): AppThunkAction<KnownDataAction> => (dispatch/*, getState*/) => {
        dispatch({ type: 'FILEUPLOAD', dest: { timeSheetEntry: null, sale: null, invoice: null, consultantInvoice: Entry, projectAssigment: null } });
    },
    requestFileUploadClose: (): AppThunkAction<KnownDataAction> => (dispatch/*, getState*/) => {
        dispatch({ type: 'FILEUPLOAD', dest: null });
    },
    patchTimeSheetEntryEdition: (timeSheetEntry: TimeSheetEntry): AppThunkAction<KnownDataAction> => (dispatch/*, getState*/) => {
        dispatch({ type: 'PATCH_TIMESHEET', timeSheetEntry: timeSheetEntry });
    },
    reloadTimeSheetEntry: (msalInstance: IPublicClientApplication, teamsState: TeamsState.TeamsState | undefined, timeSheetEntryId: string): AppThunkAction<KnownDataAction> => (dispatch, getState) => {
        // Only load data if it's something we don't already have (and are not already loading)
        const appState = getState();
        if (appState && appState.userNeomyticLogin) {
            TokenHelper.GetToken(msalInstance, teamsState)
                .then(token => {
                    if (token !== null) {
                        fetch(`api/TimeSheets/` + timeSheetEntryId, {
                            method: "get",
                            headers: {
                                "Content-Type": "application/json",
                                authorization: "bearer " + token,
                            },
                            mode: "cors",
                            cache: "default",
                        })
                            .then(response => {
                                if (response.status === 200) {
                                    return response.json() as Promise<TimeSheetEntry>;
                                }
                            })
                            .then(data => {
                                if (data) dispatch({ type: 'PATCH_TIMESHEET', timeSheetEntry: data });
                            });
                    }
                });
        }
    },
    reloadInvoice: (msalInstance: IPublicClientApplication, teamsState: TeamsState.TeamsState | undefined, invoiceId: string): AppThunkAction<KnownDataAction> => (dispatch, getState) => {
        // Only load data if it's something we don't already have (and are not already loading)
        const appState = getState();
        if (appState && appState.userNeomyticLogin) {
            TokenHelper.GetToken(msalInstance, teamsState)
                .then(token => {
                    if (token !== null) {
                        fetch(`api/Invoices/` + invoiceId, {
                            method: "get",
                            headers: {
                                "Content-Type": "application/json",
                                authorization: "bearer " + token,
                            },
                            mode: "cors",
                            cache: "default",
                        })
                            .then(response => {
                                if (response.status === 200) {
                                    return response.json() as Promise<Invoice>;
                                }
                            })
                            .then(data => {
                                if (data) dispatch({ type: 'PATCH_INVOICES', invoice: data });
                            });
                    }
                });
        }
    },
    patchTimeSheetDeletionEntryEdition: (timeSheetEntry: TimeSheetEntry): AppThunkAction<KnownDataAction> => (dispatch/*, getState*/) => {
        dispatch({ type: 'PATCHDELETE_TIMESHEET', timeSheetEntry: timeSheetEntry });
    },
    ReloadInvoicesEdition: (): AppThunkAction<KnownDataAction> => (dispatch/*, getState*/) => {
        dispatch({ type: 'RELOAD_INVOICES' });
    },
    requestInvoiceEdition: (invoice: Invoice | null): AppThunkAction<KnownDataAction> => (dispatch/*, getState*/) => {
        dispatch({ type: 'EDIT_INVOICES', invoice: invoice });
    },
    requestInvoiceGeneration: (invoice: Invoice | null): AppThunkAction<KnownDataAction> => (dispatch/*, getState*/) => {
        dispatch({ type: 'GEN_INVOICES', invoice: invoice });
    },
    requestInvoiceEmail: (invoice: Invoice | null): AppThunkAction<KnownDataAction> => (dispatch/*, getState*/) => {
        dispatch({ type: 'EMAIL_INVOICES', invoice: invoice });
    },
    requestInvoiceSendUBL: (invoice: Invoice | null): AppThunkAction<KnownDataAction> => (dispatch/*, getState*/) => {
        dispatch({ type: 'SENDUBL_INVOICES', invoice: invoice });
    },
    requestInvoiceDeletion: (invoice: Invoice | null): AppThunkAction<KnownDataAction> => (dispatch/*, getState*/) => {
        dispatch({ type: 'DELETE_INVOICES', invoice: invoice });
    },
    patchInvoiceDeletionEntryEdition: (invoice: Invoice): AppThunkAction<KnownDataAction> => (dispatch/*, getState*/) => {
        dispatch({ type: 'PATCHDELETE_INVOICES', invoice: invoice });
    },
    patchInvoiceEdition: (invoice: Invoice): AppThunkAction<KnownDataAction> => (dispatch/*, getState*/) => {
        dispatch({ type: 'PATCH_INVOICES', invoice: invoice });
    },
    requestSaleEdition: (sale: Sale | null): AppThunkAction<KnownDataAction> => (dispatch/*, getState*/) => {
        dispatch({ type: 'EDIT_SALES', sale: sale });
    },
    patchSaleEdition: (sale: Sale): AppThunkAction<KnownDataAction> => (dispatch/*, getState*/) => {
        dispatch({ type: 'PATCH_SALES', sale: sale });
    },
    requestClearQuickView: (): AppThunkAction<KnownDataAction> => (dispatch/*, getState*/) => {
        dispatch({ type: 'QV_CLEAR' });
    },
    requestInvoiceQuickView: (invoiceRef: Invoice | null): AppThunkAction<KnownDataAction> => (dispatch/*, getState*/) => {
        dispatch({ type: 'QV_INVOICE', invoiceRef: invoiceRef });
    },
    requestProjectQuickView: (project: Project | null): AppThunkAction<KnownDataAction> => (dispatch/*, getState*/) => {
        dispatch({ type: 'QV_PROJECT', project: project });
    },
    requestProjectAssigmentQuickView: (projectAssigment: ProjectAssigment | null): AppThunkAction<KnownDataAction> => (dispatch/*, getState*/) => {
        dispatch({ type: 'QV_PROJECTASSIGMENT', projectAssigment: projectAssigment });
    },
    requestTimesheetQuickView: (timeSheetEntry: TimeSheetEntry | null): AppThunkAction<KnownDataAction> => (dispatch/*, getState*/) => {
        dispatch({ type: 'QV_TIMESHEET', timeSheetEntry: timeSheetEntry });
    },
    requestProductQuickView: (product: Product | null): AppThunkAction<KnownDataAction> => (dispatch/*, getState*/) => {
        dispatch({ type: 'QV_PRODUCT', product: product });
    },
    requestSalesQuickView: (sale: Sale | null): AppThunkAction<KnownDataAction> => (dispatch/*, getState*/) => {
        dispatch({ type: 'QV_SALES', sale: sale });
    },
    editReceivedPeppolDoc: (peppolReceivedInvoice: ConsultantPeppolReceivedDoc | null): AppThunkAction<KnownDataAction> => (dispatch/*, getState*/) => {
        dispatch({ type: 'EDIT_PEPPOLRECEIVEDINVOICE', peppolReceivedDoc: peppolReceivedInvoice });
    },
};

// ----------------
// REDUCER - For a given state and action, returns the new state. To support time travel, this must not mutate the old state.

export const reducer: Reducer<DataState> = (state: DataState | undefined, incomingAction: Action): DataState => {
    if (state === undefined) {
        return {
            projects: undefined, projectsLoading: false, projectEdit: null, projectContactsEdit: null, projectDelete: null,
            projectsAssigments: undefined, projectsAssigmentsLoading: false, projectAssigmentEdit: null, projectAssigmentDelete: null,
            timeSheetEntries: undefined, timeSheetEntriesLoading: false, timeSheetEdit: null, timeSheetDelete: null,
            invoices: undefined, invoicesLoading: false, invoiceEdit: null, invoiceToDelete: null, invoiceGeneration: null, invoiceEmail: null, invoiceSendUBL: null,
            invoiceTemplates: undefined, invoiceTemplatesLoading: false,
            products: undefined, productsLoading: false,
            articles: undefined, articlesLoading: false,
            sales: undefined, salesLoading: false, salesEdit: null,
            FileUpload: null,
            invoiceQuickView: null, projectQuickView: null, projectAssigmentQuickView: null, timesheetQuickView: null, productQuickView: null, salesQuickView: null,
            peppolReceivedDocEdit: null
        };
    }

    const action = incomingAction as KnownDataAction;
    switch (action.type) {
        case 'RELOAD_PROJECTS':
            return { ...state, projects: undefined };
        case 'REQUEST_PROJECTS':
            return { ...state, projectsLoading: true };
        case 'RECEIVE_PROJECTS':
            return { ...state, projectsLoading: false, projects: action.projects };
        case 'REQUEST_PROJECTCONTACTS': {
            const ns = { ...state };
            if (ns.projects) {
                const c = ns.projects.findIndex(e => e.projectid === action.projectid);
                if (c !== -1) {
                    ns.projects[c].contactsLoading = true;
                    // If quickView element is the one we update, force update
                    if (state.projectQuickView && state.projectQuickView.projectid === action.projectid) {
                        ns.projectQuickView = { ...ns.projects[c] };
                    }
                    if (state.projectContactsEdit && state.projectContactsEdit.projectid === action.projectid) {
                        ns.projectContactsEdit = { ...ns.projects[c] };
                    }
                    ns.projects = [...ns.projects];
                    return ns;
                }
            }
            return state;
        }
        case 'RECEIVE_PROJECTCONTACTS': {
            const ns = { ...state };
            if (ns.projects) {
                const c = ns.projects.findIndex(e => e.projectid === action.projectid);
                if (c !== -1) {
                    ns.projects[c].contacts = action.contactProjectRoles;
                    ns.projects[c].contactsLoading = false;
                    // If quickView element is the one we update, force update
                    if (state.projectQuickView && state.projectQuickView.projectid === action.projectid) {
                        ns.projectQuickView = { ...ns.projects[c] };
                    }
                    if (state.projectContactsEdit && state.projectContactsEdit.projectid === action.projectid) {
                        ns.projectContactsEdit = { ...ns.projects[c] };
                    }
                    ns.projects = [...ns.projects];
                    return ns;
                }
            }
            return state;
        }

        case 'EDIT_PROJECT':
            return { ...state, projectEdit: action.project };
        case 'DELETE_PROJECT':
            return { ...state, projectDelete: action.project };
        case 'PATCH_PROJECT': {
            const ns = { ...state };
            if (ns.projects && action.project.customerid) {
                const c = ns.projects.findIndex(e => e.projectid === action.project.projectid);
                if (c !== -1) {
                    ns.projects[c] = action.project;
                    // If quickView element is the one we update, force update
                    if (state.projectQuickView && state.projectQuickView.projectid === action.project.projectid) {
                        ns.projectQuickView = { ...action.project };
                    }
                }
                else {
                    ns.projects.push(action.project);
                }
                ns.projects = [...ns.projects];
                return ns;
            }
            return state;
        }
        case 'PATCHDELETE_PROJECT': {
            const ns = { ...state };
            if (ns.projects && action.project.projectid) {
                ns.projects = ns.projects.filter((item) => item.projectid !== action.project.projectid);
                ns.projects = [...ns.projects];
                return ns;
            }
            return state;
        }
        case 'EDIT_PROJECTCONTACTS':
            return { ...state, projectContactsEdit: action.project };
        case 'RELOAD_PROJECTASSIGMENT':
            return { ...state, projectsAssigments: undefined };
        case 'REQUEST_PROJECTSASSIGMENTS':
            return { ...state, projectsAssigmentsLoading: true };
        case 'RECEIVE_PROJECTSASSIGMENTS':
            return { ...state, projectsAssigmentsLoading: false, projectsAssigments: action.projectAssigments };
        case 'EDIT_PROJECTASSIGMENT':
            return { ...state, projectAssigmentEdit: action.projectAssigment };
        case 'DELETE_PROJECTASSIGMENT':
            return { ...state, projectAssigmentDelete: action.projectAssigment };
        case 'PATCH_PROJECTASSIGMENT': {
            const ns = { ...state };
            if (ns.projectsAssigments && action.projectAssigment.projectAssignmentid) {
                const c = ns.projectsAssigments.findIndex(e => e.projectAssignmentid === action.projectAssigment.projectAssignmentid);
                if (c !== -1) {
                    ns.projectsAssigments[c] = action.projectAssigment;
                    // If quickView element is the one we update, force update
                    if (state.projectAssigmentQuickView && state.projectAssigmentQuickView.projectAssignmentid === action.projectAssigment.projectAssignmentid) {
                        ns.projectAssigmentQuickView = { ...action.projectAssigment };
                    }
                }
                else {
                    ns.projectsAssigments.push(action.projectAssigment);
                }
                ns.projectsAssigments = [...ns.projectsAssigments];
                return ns;
            }
            return state;
        }
        case 'PATCHDELETE_PROJECTASSIGMENT': {
            const ns = { ...state };
            if (ns.projectsAssigments && action.projectAssigment.projectAssignmentid) {
                ns.projectsAssigments = ns.projectsAssigments.filter((item) => item.projectAssignmentid !== action.projectAssigment.projectAssignmentid);
                ns.projectsAssigments = [...ns.projectsAssigments];
                return ns;
            }
            return state;
        }
        case 'REQUEST_TIMESHEETENTRIES':
            return { ...state, timeSheetEntriesLoading: true };
        case 'RECEIVE_TIMESHEETENTRIES':
            return { ...state, timeSheetEntriesLoading: false, timeSheetEntries: action.timeSheetEntries.sort(SortHelper.TimeSheetByDate_Desc) };
        case 'RELOAD_TIMESHEETS':
            return { ...state, timeSheetEntries: undefined };
        case 'EDIT_TIMESHEET':
            return { ...state, timeSheetEdit: action.timeSheetEntry };
        case 'DELETE_TIMESHEET':
            return { ...state, timeSheetDelete: action.timeSheetEntry };
        case 'PATCHDELETE_TIMESHEET': {
            const ns = { ...state };
            if (ns.timeSheetEntries && action.timeSheetEntry.timesheetEntryid) {
                ns.timeSheetEntries = ns.timeSheetEntries.filter((item) => item.timesheetEntryid !== action.timeSheetEntry.timesheetEntryid);
                ns.timeSheetEntries = [...ns.timeSheetEntries.sort(SortHelper.TimeSheetByDate_Desc)];
                return ns;
            }
            return state;
        }
        case 'PATCH_TIMESHEET': {
            const ns = { ...state };
            if (ns.timeSheetEntries && action.timeSheetEntry.timesheetEntryid) {
                const c = ns.timeSheetEntries.findIndex(e => e.timesheetEntryid === action.timeSheetEntry.timesheetEntryid);
                if (c !== -1) {
                    ns.timeSheetEntries[c] = action.timeSheetEntry;
                    // If quickView element is the one we update, force update
                    if (state.timesheetQuickView && state.timesheetQuickView.timesheetEntryid === action.timeSheetEntry.timesheetEntryid) {
                        ns.timesheetQuickView = { ...action.timeSheetEntry };
                    }
                }
                else {
                    ns.timeSheetEntries.push(action.timeSheetEntry);
                }
                ns.timeSheetEntries = [...ns.timeSheetEntries.sort(SortHelper.TimeSheetByDate_Desc)];
                return ns;
            }
            return state;
        }
        case 'RELOAD_INVOICES':
            return { ...state, invoices: undefined };
        case 'REQUEST_INVOICES':
            return { ...state, invoicesLoading: true };
        case 'RECEIVE_INVOICES':
            return { ...state, invoicesLoading: false, invoices: action.invoices.sort(SortHelper.InvoiceByName_Desc) };
        case 'EDIT_INVOICES':
            return { ...state, invoiceEdit: action.invoice };
        case 'DELETE_INVOICES':
            return { ...state, invoiceToDelete: action.invoice };
        case 'PATCHDELETE_INVOICES': {
            const ns = { ...state };
            if (ns.invoices && action.invoice.invoiceid) {
                ns.invoices = ns.invoices.filter((item) => item.invoiceid !== action.invoice.invoiceid);
                ns.invoices = [...ns.invoices.sort(SortHelper.InvoiceByName_Desc)];
                return ns;
            }
            return state;
        }
        case 'GEN_INVOICES':
            return { ...state, invoiceGeneration: action.invoice };
        case 'EMAIL_INVOICES':
            return { ...state, invoiceEmail: action.invoice };
        case 'SENDUBL_INVOICES':
            return { ...state, invoiceSendUBL: action.invoice };
        case 'PATCH_INVOICES': {
            const ns = { ...state };
            if (ns.invoices && action.invoice.invoiceid) {
                const c = ns.invoices.findIndex(e => e.invoiceid === action.invoice.invoiceid);
                if (c !== -1) {
                    ns.invoices[c] = action.invoice;
                    if (state.invoiceQuickView && state.invoiceQuickView.invoiceid === action.invoice.invoiceid) {
                        // update the quick view object
                        ns.invoiceQuickView = { ...action.invoice };
                    }
                }
                else {
                    ns.invoices.push(action.invoice);
                }
                ns.invoices = [...ns.invoices.sort(SortHelper.InvoiceByName_Desc)];
                return ns;
            }
            return state;
        }
        case 'REQUEST_INVOICETEMPLATES':
            return { ...state, invoiceTemplatesLoading: true };
        case 'RECEIVE_INVOICEETEMPLATES':
            return { ...state, invoiceTemplatesLoading: false, invoiceTemplates: action.invoiceTemplates.sort(SortHelper.InvoiceTemplateByOrder) };
        case 'REQUEST_PRODUCTS':
            return { ...state, productsLoading: true };
        case 'RECEIVE_PRODUCTS':
            return { ...state, productsLoading: false, products: action.products };
        case 'REQUEST_ARTICLES':
            return { ...state, articlesLoading: true };
        case 'RECEIVE_ARTICLES':
            return { ...state, articlesLoading: false, articles: action.articles };
        case 'REQUEST_SALES':
            return { ...state, salesLoading: true };
        case 'RECEIVE_SALES':
            return { ...state, salesLoading: false, sales: action.sales };
        case 'EDIT_SALES':
            return { ...state, salesEdit: action.sale };
        case 'PATCH_SALES': {
            const ns = { ...state };
            if (ns.sales && action.sale.sellid) {
                const c = ns.sales.findIndex(e => e.sellid === action.sale.sellid);
                if (c !== -1) {
                    ns.sales[c] = action.sale;
                }
                else {
                    ns.sales.push(action.sale);
                }
                return ns;
            }
            return state;
        }

        case 'FILEUPLOAD':
            return { ...state, FileUpload: action.dest };

        case 'QV_CLEAR': {
            return { ...state, invoiceQuickView: null, projectQuickView: null, projectAssigmentQuickView: null, timesheetQuickView: null, productQuickView: null, salesQuickView: null };
        }
        case 'QV_INVOICE': {
            return { ...state, invoiceQuickView: action.invoiceRef, projectQuickView: null, projectAssigmentQuickView: null, timesheetQuickView: null, productQuickView: null, salesQuickView: null };
        }
        case 'QV_PROJECT': {
            return { ...state, invoiceQuickView: null, projectQuickView: action.project, projectAssigmentQuickView: null, timesheetQuickView: null, productQuickView: null, salesQuickView: null };
        }
        case 'QV_PROJECTASSIGMENT': {
            return { ...state, invoiceQuickView: null, projectQuickView: null, projectAssigmentQuickView: action.projectAssigment, timesheetQuickView: null, productQuickView: null, salesQuickView: null };
        }
        case 'QV_TIMESHEET': {
            return { ...state, invoiceQuickView: null, projectQuickView: null, projectAssigmentQuickView: null, timesheetQuickView: action.timeSheetEntry, productQuickView: null, salesQuickView: null };
        }
        case 'QV_PRODUCT': {
            return { ...state, invoiceQuickView: null, projectQuickView: null, projectAssigmentQuickView: null, timesheetQuickView: null, productQuickView: action.product, salesQuickView: null };
        }
        case 'QV_SALES': {
            return { ...state, invoiceQuickView: null, projectQuickView: null, projectAssigmentQuickView: null, timesheetQuickView: null, productQuickView: null, salesQuickView: action.sale };
        }
        case 'EDIT_PEPPOLRECEIVEDINVOICE': {
            return { ...state, peppolReceivedDocEdit: action.peppolReceivedDoc };
        }
        default: {
            break;
        }
    }
    // For the QuickView is selected on an other, clear this one
    const actionContact = incomingAction as KnownContactsAction;
    switch (actionContact.type) {
        case 'QV_CONTACT': {
            return { ...state, invoiceQuickView: null, projectQuickView: null, projectAssigmentQuickView: null, timesheetQuickView: null, productQuickView: null, salesQuickView: null };
        }
        default: {
            break;
        }
    }
    const actionConsultant = incomingAction as KnownConsultantAction;
    switch (actionConsultant.type) {
        case 'QV_CONSULTANT': {
            return { ...state, invoiceQuickView: null, projectQuickView: null, projectAssigmentQuickView: null, timesheetQuickView: null, productQuickView: null, salesQuickView: null };
        }
        default: {
            break;
        }
    }
    const actionCustomer = incomingAction as KnownCustomersAction;
    switch (actionCustomer.type) {
        case 'EDIT_CUSTOMERCONTACTS': {
            if (actionCustomer.customer === null) return { ...state, projectContactsEdit: null };
            break;
        }
        case 'QV_CUSTOMER': {
            return { ...state, invoiceQuickView: null, projectQuickView: null, projectAssigmentQuickView: null, timesheetQuickView: null, productQuickView: null, salesQuickView: null };
        }
        default: {
            break;
        }
    }
    return state;
};